<template>
	<Modal 
		v-if="showModal" 
		:allow-close="true" 
		:max-width="'max-w-[691px]'" 
		:hide-actions="true" 
		:prevent-scrolling="false"
		@modal:close="closeModal"
	>
		<template #body>
			<div class="pb-[60px]">
				<div class="text-won-blue">
					<h2 class="text-center text-[24px] leading-[34px] font-semibold">
						{{ title }}
					</h2>
					<div v-show="!cancelPlan">
						<p class="mt-2 py-3 text-md leading-[20px] font-bold">
							You will be losing:
						</p>

						<div class="grid grid-cols-2">
							<subscription-feature
								v-for="(feature, index) of plan.features"
								:key="`${plan.id}-feature-${index}`"
								:feature="feature"
							/>
						</div>
					</div>
				</div>
				<form
					v-show="cancelPlan"
					class="mt-2 w-full"
					@submit.prevent="cancelSubscription"
				>
					<div class="mt-7">
						<div
							v-for="(checkbox, index) in cancelOptions"
							:key="index"
							class="mb-2 flex items-center justify-start gap-2"
						>
							<input
								:id="checkbox.id"
								v-model="reason"
								class="mt-0 mr-1 ring-0 checked:border-none checked:text-won-green focus:ring-0"
								type="checkbox"
								:value="checkbox.value"
							/>
							<label
								:for="checkbox.id"
								class="won-color-gray cursor-pointer text-[16px] leading-[20px]"
							>
								{{ checkbox.label }}
							</label>
						</div>
						<div
							v-if="reason.includes('other')"
							class="mt-2 flex w-full flex-row justify-between gap-x-3"
						>
							<Text
								v-model="otherReason"
								:autocomplete="false"
								placeholder="Other (Please briefly explain)"
								class="h-[36px] w-full"
								class-list="text-gray-900"
							/>
						</div>

						<div
							v-if="showError"
							class="mt-4 text-[12px] text-red-500"
						>{{ errorMessage }}</div>

					</div>

					<div
						v-if="cancelPlan"
						class="mt-[19px] flex justify-center"
					>
						<won-button class="w-full">
							<svg
								v-if="submitting"
								class="mr-3 -ml-1 size-5 animate-spin text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								/>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
							Submit
						</won-button>
					</div>
				</form>


				<div
					v-if="!cancelPlan"
					class="mt-[19px] flex justify-end"
				>
					<button
						class="w-32 rounded p-[9.5px] text-base text-won-dark"
						@click="closeModal"
					>
						Cancel
					</button>
    
					<button
						class="w-64 rounded bg-won-red-light p-[9.5px] text-base text-won-red"
						@click="handleProceedToCancel"
					>
						Lose Premium Features
					</button>
				</div>


			</div>
		</template>
	</Modal>
</template>

<script setup>

import { ref, nextTick, watch, computed } from 'vue';
import Modal from '../../modal';
import Text from '../../../forms/elements/text';
import { useSubscriptionPlanStore } from '../../../store/subscription.store';
import SubscriptionFeature from './subscription-feature';
import { useNotifications } from '../../../store/notifications.store';


import WonButton from '../../won-button.vue';

const submitting = ref(false);
const subscriptionPlanStore = useSubscriptionPlanStore();
const cancelPlan = ref(false);
const showError = ref(false);
const errorMessage = ref('');
const title = ref('Are you sure you want to cancel your plan?');
const reason = ref([]);
const otherReason = ref('');
const emit = defineEmits(['cancel-subscription:proceed']);

const plan = computed(() => {
	const userStripePrice = subscriptionPlanStore?.userPlan?.stripe_price;
	return subscriptionPlanStore.fetchPlan(userStripePrice) ?? subscriptionPlanStore.fetchPlan('price_premium');
});

const cancelOptions = ref([
	{ id: 'got_job', label: 'I got a job!', value: 'I got a job!', checked: false },
	{ id: 'expensive', label: 'Premium plan is too expensive', value: 'Premium plan is too expensive', checked: false },
	{ id: 'no_longer_need_premium', label: 'No longer need premium features', value: 'No longer need premium features', checked: false },
	{ id: 'not_satisfied', label: 'Not satisfied with premium features', value: 'Not satisfied with premium features', checked: false },
	{ id: 'other', label: 'Others', value: 'other', checked: false },
]);
    
const props = defineProps({
	showModal: {
		type: Boolean,
		default: false,
	},
	closeModal: {
		type: Function,
		required: true
	},
	currentPlan: {
		type: String,
		default: null
	}
});

const handleProceedToCancel = () => {
	cancelPlan.value = true;
	title.value = 'Sorry to hear that! Please let us know what went wrong so we can improve.';
};

// Reset component state when modal closes (after success or manual close)
const resetState = () => {
	submitting.value = false;
	cancelPlan.value = false;
	showError.value = false;
	errorMessage.value = '';
	title.value = 'Are you sure you want to cancel your plan?';
	reason.value = [];
	otherReason.value = '';
};

watch(() => props.showModal, (isOpen) => {
	if (!isOpen) {
		nextTick(() => {
			resetState();
		});
	}
});

const cancelSubscription = async () => {
	const notificationStore = useNotifications();
	submitting.value = true;
	showError.value = false;
	errorMessage.value = '';

	try {
		if (reason.value.length === 0) {
			errorMessage.value = 'Please select at least one reason for canceling';
			showError.value = true;
			submitting.value = false;
			return;
		}

		if (reason.value.includes('other') && otherReason.value.trim() === '') {
			errorMessage.value = 'Please explain your reason in the text field';
			showError.value = true;
			submitting.value = false;
			return;
		}

		// Create a new array to avoid mutating the reactive state
		const finalReasons = [...reason.value];

		// If "other" is selected, replace it with the actual text
		if (finalReasons.includes('other')) {
			const index = finalReasons.indexOf('other');
			finalReasons.splice(index, 1, otherReason.value);
		}

		emit('cancel-subscription:proceed', { reason: finalReasons });

	} catch (error) {
		const message = error?.response?.data?.message || error?.message || 'Something went wrong';
		notificationStore.notify(message, 'error');
		submitting.value = false;
	}
};

</script>

<style>

[type='checkbox'], [type='radio'] {
    color: #2A8176;
}
</style>

